import { useState, useEffect, useCallback, useRef } from "react";
import { Star, Play, Square, Pause, ShieldCheck, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * ⭐ CONTROLLED FOREX RISE/FALL AUTO BOT — VTS primary product.
 * 8 validated pairs · 1-min completed candles · EMA9/21 + RSI14 + ATR14 ·
 * fixed-fractional risk · pacing · daily caps · NO martingale.
 * Target 30–50 valid trades on an active Forex day — NEVER forced.
 * No profit, accuracy or win rate is guaranteed.
 */
const isDemoAcct = (id) => /^(VRTC|VRW|DOT)/i.test(id || "");

const DECISION_STYLE = {
  CALL: "text-vts-profit", PUT: "text-vts-loss",
  NO_TRADE: "text-vts-silver/60", REJECTED: "text-amber-300",
};

const Stat = ({ label, val, c = "text-white", testid }) => (
  <div data-testid={testid} className="rounded bg-vts-panel border border-vts-line p-1.5 text-center">
    <p className={`text-[11px] font-bold font-mono ${c}`}>{val}</p>
    <p className="text-[8px] font-mono text-vts-silver/50">{label}</p>
  </div>
);

const ConservativeFxBot = ({ authorized, accountId, accessToken, balance, onConnect }) => {
  const [st, setSt] = useState(null);
  const [riskPct, setRiskPct] = useState("0.25");
  const [allowMin, setAllowMin] = useState(true);
  const [recovery, setRecovery] = useState(false); // 🔁 OFF by default — user must opt in
  const [liveOk, setLiveOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [reportDays, setReportDays] = useState(7);
  const timer = useRef(null);
  const demo = isDemoAcct(accountId);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get(`/consfx/status/${accountId}`);
      setSt(res.data);
    } catch { /* ignore */ }
  }, [accountId]);

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, 6000);
    return () => clearInterval(timer.current);
  }, [poll]);

  const loadReport = useCallback(async (days) => {
    if (!accountId) return;
    try {
      const res = await api.get(`/consfx/report/${accountId}`, { params: { days } });
      setReport(res.data);
      setReportDays(days);
    } catch { /* ignore */ }
  }, [accountId]);

  const start = async () => {
    if (!authorized) { onConnect?.(); return; }
    setBusy(true);
    try {
      const res = await api.post("/consfx/start", {
        account_id: accountId, access_token: accessToken || "",
        risk_pct: Number(riskPct) || 0.25, allow_min_stake: allowMin,
        recovery_enabled: recovery,
        live_confirmed: demo ? false : liveOk,
      });
      setSt(res.data);
      toast.success("⭐ Controlled FX on — it monitors 8 pairs 24/7 but only trades valid signals. Quiet stretches are normal, never forced.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the Controlled FX bot");
    }
    setBusy(false);
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post(`/consfx/stop/${accountId}`);
      toast.success("🛑 Emergency stop — Controlled FX off");
      poll();
    } catch { toast.error("Could not stop the bot"); }
    setBusy(false);
  };

  const togglePause = async () => {
    setBusy(true);
    try {
      await api.post(`/consfx/pause/${accountId}`, { paused: !st?.paused });
      poll();
    } catch { toast.error("Could not toggle pause"); }
    setBusy(false);
  };

  const running = !!st?.running;
  const net = Number(st?.net || 0);
  const estStake = balance ? Math.max(0.35 * 0 + (balance * (Number(riskPct) || 0.25)) / 100, 0) : null;

  return (
    <div data-testid="consfx-bot" className="rounded-lg border-2 border-sky-400/40 bg-vts-surface/40 p-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
        <p className="text-xs font-bold text-white">MOST RECOMMENDED · CONTROLLED FOREX RISE/FALL AUTO BOT</p>
        {running && (
          <span data-testid="consfx-live" className={`ml-auto px-1.5 py-0.5 rounded border text-[9px] font-bold font-mono ${st?.paused ? "bg-amber-400/15 border-amber-400/40 text-amber-300" : "bg-vts-profit/15 border-vts-profit/40 text-vts-profit animate-pulse"}`}>
            {st?.paused ? "⏸ PAUSED" : "● ON"} · {st?.is_demo ? "DEMO" : "LIVE"}
          </span>
        )}
      </div>

      <p className="text-[9px] font-mono text-vts-silver/60 leading-relaxed">
        Always-on Forex Rise/Fall automation using 1-minute market monitoring, EMA9, EMA21, RSI14 and ATR14
        signal filters. Fixed-risk settings, trade pacing, daily loss protection, and no martingale.
        <span className="text-sky-300"> Target: 30–50 valid trades during an active Forex day. The bot never
        forces a trade to reach the target.</span> 24/7 means continuous monitoring — not continuous trading.
      </p>
      <p data-testid="consfx-risk-warning" className="text-[9px] font-mono text-amber-300/90 rounded border border-amber-400/30 bg-amber-400/5 px-2 py-1.5">
        ⚠️ Trading involves risk. This bot does not guarantee profit, accuracy, or successful trades. Users can lose money.
      </p>

      {running ? (
        <>
          <div data-testid="consfx-stats" className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <Stat testid="consfx-today" label={`Trades today / ${st?.daily_cap ?? 50}`} val={`${st?.trades_today ?? 0}`} c={st?.cap_reached ? "text-amber-300" : "text-sky-300"} />
            <Stat testid="consfx-net" label="Net" val={`${net >= 0 ? "+" : "−"}$${Math.abs(net).toFixed(2)}`} c={net >= 0 ? "text-vts-profit" : "text-vts-loss"} />
            <Stat testid="consfx-wl" label="W / L" val={`${st?.wins ?? 0}/${st?.losses ?? 0}`} />
            <Stat testid="consfx-consec" label={`Consec losses (max ${st?.max_consec_losses ?? 3})`} val={`${st?.consec_losses ?? 0}`} c={(st?.consec_losses ?? 0) >= 2 ? "text-amber-300" : "text-white"} />
            <Stat testid="consfx-lossleft" label="Daily loss left" val={st?.daily_loss_remaining != null ? `$${st.daily_loss_remaining}` : "—"} />
            <Stat testid="consfx-open" label={`Open / ${st?.max_open_total ?? 3}`} val={`${st?.open_count ?? 0}`} />
          </div>
          {st?.recovery_enabled && (
            <p data-testid="consfx-recovery-status" className="text-[9px] font-mono text-orange-300 text-center">
              🔁 Recovery ON (2-slice +12%) {Number(st?.deficit) > 0 ? `— deficit $${Number(st.deficit).toFixed(2)} being clawed back` : "— no open deficit"}
            </p>
          )}
          {st?.cap_reached && (
            <p data-testid="consfx-cap-msg" className="text-[9px] font-mono text-amber-300 text-center">
              Daily trade cap reached. New trades are paused until the next active day.
            </p>
          )}
          {st?.account_paused_for_s > 0 && (
            <p className="text-[9px] font-mono text-amber-300 text-center">
              🛑 Account paused after consecutive losses — resumes in ~{Math.ceil(st.account_paused_for_s / 60)}m. No recovery trades.
            </p>
          )}

          {/* per-pair board */}
          <div data-testid="consfx-pairs" className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {(st?.pairs || []).map((p) => (
              <div key={p.symbol} className={`rounded border p-1.5 ${p.open ? "border-sky-400/50 bg-sky-400/10" : "border-vts-line/50 bg-vts-panel"}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold font-mono text-white">{p.label}</p>
                  <span className="text-[8px] font-mono text-vts-silver/50">
                    {p.open ? "📈 open" : p.paused_for_s > 0 ? `⏸ ${Math.ceil(p.paused_for_s / 60)}m` : p.cooldown_s > 0 ? `⏳ ${Math.ceil(p.cooldown_s / 60)}m` : p.enabled ? `${p.duration_min}m` : "off"}
                  </span>
                </div>
                <p className="text-[8px] font-mono text-vts-silver/60">
                  {p.indicators?.rsi != null ? `RSI ${p.indicators.rsi.toFixed(0)} · ${p.indicators.ema9 > p.indicators.ema21 ? "EMA↑" : "EMA↓"}` : "…"}
                </p>
                <p className="text-[7.5px] font-mono text-vts-silver/45 truncate" title={p.last_reason}>{p.last_reason}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button data-testid="consfx-pause-btn" onClick={togglePause} disabled={busy}
              className="py-2 rounded-md bg-amber-500/80 hover:bg-amber-500 text-black text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
              {st?.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
              {st?.paused ? "RESUME AUTOMATION" : "PAUSE AUTOMATION"}
            </button>
            <button data-testid="consfx-stop-btn" onClick={stop} disabled={busy}
              className="py-2 rounded-md bg-vts-loss/80 hover:bg-vts-loss text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
              <Square className="w-3.5 h-3.5" /> EMERGENCY STOP
            </button>
          </div>

          {(st?.feed || []).length > 0 && (
            <div data-testid="consfx-feed" className="space-y-0.5 max-h-24 overflow-y-auto">
              {[...st.feed].reverse().slice(0, 8).map((f, i) => (
                <p key={i} className="text-[9px] font-mono text-vts-silver/55">
                  <span className="text-vts-silver/35">{String(f.ts).slice(11, 19)}</span> {f.text}
                </p>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* settings before automation */}
          <div data-testid="consfx-settings" className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[8px] font-mono text-vts-silver/50">Risk per trade % of balance (max 0.50)</span>
              <input data-testid="consfx-risk" type="number" min="0.05" max="0.5" step="0.05" value={riskPct}
                onChange={(e) => setRiskPct(e.target.value)}
                className="w-full mt-0.5 px-2 py-1.5 rounded bg-vts-panel border border-vts-line text-xs font-mono text-white" />
            </label>
            <div className="rounded bg-vts-panel border border-vts-line p-1.5 text-center">
              <p className="text-[10px] font-bold font-mono text-white">
                {!authorized ? "🔌 Not connected" : demo ? "🧪 DEMO account" : "💵 REAL account"}
              </p>
              <p className="text-[8px] font-mono text-vts-silver/50">
                {!authorized ? "connect Deriv (demo first recommended)"
                  : estStake != null ? `est. stake ≈ $${Math.max(estStake, allowMin ? 0.35 : estStake).toFixed(2)}` : demo ? "paper money — recommended first" : "live activation required below"}
              </p>
            </div>
          </div>
          <label data-testid="consfx-allow-min" className="flex items-start gap-2 text-[9px] font-mono text-vts-silver/60 cursor-pointer">
            <input type="checkbox" checked={allowMin} onChange={(e) => setAllowMin(e.target.checked)} className="mt-0.5" />
            <span>Allow the $0.35 provider minimum stake when my risk % rounds below it (otherwise those trades are rejected)</span>
          </label>
          {/* 🔁 Optional recovery — exact Over 2 mechanism, OFF by default */}
          <label data-testid="consfx-recovery" className={`flex items-start gap-2 rounded border p-2 cursor-pointer transition ${recovery ? "border-orange-400/60 bg-orange-400/10" : "border-vts-line/60 bg-vts-panel"}`}>
            <input type="checkbox" checked={recovery} onChange={(e) => setRecovery(e.target.checked)}
              data-testid="consfx-recovery-checkbox" className="mt-0.5" />
            <span className="text-[9px] font-mono leading-snug">
              <span className={`font-bold ${recovery ? "text-orange-300" : "text-vts-silver/70"}`}>
                🔁 Recovery mode (2-slice +12% — same as the Over 2 bot) · {recovery ? "ON — riskier" : "OFF (default)"}
              </span>
              <span className="text-vts-silver/55 block">
                After a loss, the next stakes target the deficit ×1.12 split into 2 slices instead of staying flat.
                Recovery raises stakes after losses and can drain a balance faster. Daily/weekly loss caps still apply. Leave OFF unless you accept the extra risk.
              </span>
            </span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-center">
            {[
              ["Pairs", "8 validated FX"], ["Target / cap", "30–50 / 50 hard"],
              ["Daily loss cap", "1% · weekly 3%"], ["Losses pause", "3 in a row · 30m"],
            ].map(([l, v]) => (
              <div key={l} className="rounded bg-vts-panel border border-vts-line p-1.5">
                <p className="text-[9px] font-bold font-mono text-white">{v}</p>
                <p className="text-[7.5px] font-mono text-vts-silver/50">{l}</p>
              </div>
            ))}
          </div>
          <p data-testid="consfx-markup" className="text-[8.5px] font-mono text-vts-silver/55">
            💠 Markup disclosure: VTS earns a 1% app markup per trade (charged inside the Deriv buy price).
            The exact amount is shown on every trade record. Markup is never hidden.
          </p>
          {!demo && authorized && (
            <label data-testid="consfx-live-confirm" className="flex items-start gap-2 rounded border border-amber-400/40 bg-amber-400/5 p-2 cursor-pointer">
              <input type="checkbox" checked={liveOk} onChange={(e) => setLiveOk(e.target.checked)}
                data-testid="consfx-live-confirm-checkbox" className="mt-0.5" />
              <span className="text-[9px] font-mono text-amber-200/90 leading-snug">
                I understand this trades REAL money on {accountId}, results are not guaranteed, and losses are possible.
                I choose to activate LIVE trading.
              </span>
            </label>
          )}
          <button data-testid="consfx-start-btn" onClick={start} disabled={busy}
            className="w-full py-2 rounded-md bg-sky-500/85 hover:bg-sky-500 text-black text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Play className="w-3.5 h-3.5" /> START CONTROLLED FX {demo ? "(DEMO)" : authorized ? "(LIVE)" : "— CONNECT FIRST"}
          </button>
          {st?.stopped_reason && (
            <p data-testid="consfx-stopped" className="text-[9px] font-mono text-amber-300/80 text-center">
              Last run ended: {String(st.stopped_reason).replace(/_/g, " ")}
            </p>
          )}
        </>
      )}

      {/* 🔎 decision log */}
      {(st?.evals || []).length > 0 && (
        <div data-testid="consfx-evals" className="space-y-1 max-h-40 overflow-y-auto">
          <p className="text-[9px] font-mono text-vts-silver/50 uppercase flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Decision log — signals AND rejections
          </p>
          {st.evals.map((e, i) => (
            <div key={i} className="flex items-start gap-2 text-[9px] font-mono rounded border border-vts-line/40 bg-vts-panel px-2 py-1">
              <span className="text-vts-silver/45 shrink-0">{String(e.ts || "").slice(11, 16)}</span>
              <span className="text-white shrink-0">{e.pair || ""}</span>
              <span className={`font-bold shrink-0 ${DECISION_STYLE[e.decision] || "text-white"}`}>{e.decision}</span>
              <span className="text-vts-silver/60 leading-snug">{e.reason}</span>
            </div>
          ))}
        </div>
      )}

      {/* 📜 trade history with markup transparency */}
      {(st?.recent || []).length > 0 && (
        <div data-testid="consfx-history" className="space-y-1 max-h-36 overflow-y-auto">
          <p className="text-[9px] font-mono text-vts-silver/50 uppercase">Trade history (net after markup)</p>
          {st.recent.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] font-mono rounded border border-vts-line/40 bg-vts-panel px-2 py-1">
              <span className="text-vts-silver/45">{String(r.settled_at || "").slice(5, 16).replace("T", " ")}</span>
              <span className="text-white">{r.pair || ""} {r.contract_type === "CALL" ? "▲" : "▼"}</span>
              <span className="text-vts-silver/40">${Number(r.stake).toFixed(2)}</span>
              {r.markup_amount != null && <span className="text-vts-silver/40">mkup ${Number(r.markup_amount).toFixed(2)}</span>}
              <span className={`ml-auto font-bold ${Number(r.profit) > 0 ? "text-vts-profit" : "text-vts-loss"}`}>
                {Number(r.profit) > 0 ? "+" : "−"}${Math.abs(Number(r.profit)).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* 📊 honest performance report */}
      <div data-testid="consfx-report" className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3 h-3 text-vts-silver/50" />
          <p className="text-[9px] font-mono text-vts-silver/50 uppercase">Performance (net after markup — never wins-only)</p>
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`consfx-report-${d}d`} onClick={() => loadReport(d)}
              className={`px-1.5 py-0.5 rounded text-[9px] font-mono border ${reportDays === d && report ? "border-sky-400/60 text-sky-300" : "border-vts-line text-vts-silver/50 hover:text-white"}`}>
              {d}d
            </button>
          ))}
        </div>
        {report && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            <Stat label="Trades" val={report.trades} />
            <Stat label="Win rate" val={report.win_rate != null ? `${report.win_rate}%` : "—"} />
            <Stat label="Net after markup" val={`${report.net_after_markup >= 0 ? "+" : "−"}$${Math.abs(report.net_after_markup).toFixed(2)}`} c={report.net_after_markup >= 0 ? "text-vts-profit" : "text-vts-loss"} />
            <Stat label="Markup charged" val={`$${report.total_markup}`} />
            <Stat label="Max drawdown" val={`$${report.max_drawdown}`} />
            <Stat label="Worst streak" val={report.longest_losing_streak} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConservativeFxBot;
